<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, useTemplateRef, watch, type Ref } from "vue";

import RichTextEditor from "@/components/ui/RichTextEditor.vue";
import SimpleButton from "@/components/ui/SimpleButton.vue";
import TextInput from "@/components/ui/TextInput.vue";
import { useModalsStore } from "@/stores/modals";

const modalsStore = useModalsStore();

const modal = computed(() => modalsStore.getCurrentModal());

const promptInput = useTemplateRef<InstanceType<typeof TextInput>>("promptInput");
const richTextEditor = useTemplateRef<InstanceType<typeof RichTextEditor>>("richTextEditor");

const promptResponse = ref<string>("");
const isPromptConfirmButtonDisabled = ref<boolean>(false);

function modelBindings(models?: Record<string, Ref<unknown>>) {
  if (!models) return {};
  const bindings: Record<string, unknown> = {};
  for (const [key, model] of Object.entries(models)) {
    bindings[key] = model.value;
    bindings[`onUpdate:${key}`] = (value: unknown) => {
      model.value = value;
    };
  }
  return bindings;
}

function onKeyDown(event: KeyboardEvent) {
  if (event.key === "Escape") {
    cancel();
  } else if (
    event.key === "Enter" &&
    modal.value?.kind === "builtin" &&
    modal.value?.type !== "multiline-prompt"
  ) {
    event.preventDefault();
    event.stopPropagation();
    confirm();
  }
}

function cancel() {
  const m = modal.value;
  if (!m) return;

  if (m.kind === "component") {
    m.cancel();
  } else {
    m.onCancel();
  }
  promptResponse.value = "";
}

function confirm() {
  const m = modal.value;
  if (!m || m.kind === "component") return;

  if (m.type === "prompt" || m.type === "multiline-prompt") {
    m.response = promptResponse.value;
  }
  m.onConfirm();
  promptResponse.value = "";
}

function updatePromptConfirmButtonDisabledState() {
  isPromptConfirmButtonDisabled.value =
    !promptInput.value?.checkValidity() || promptResponse.value.trim().length === 0;
}

watch([promptResponse, modal], () => {
  updatePromptConfirmButtonDisabledState();
});

onMounted(() => {
  document.addEventListener("keydown", onKeyDown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", onKeyDown);
});
</script>

<template>
  <Transition name="modal-appear">
    <div v-if="modal" class="container">
      <div class="dialog shadow-lg">
        <div class="header">
          <div class="text-base-medium">
            <i v-if="(modal.titleIcon ?? '').length > 0" class="icon" :class="modal.titleIcon" />
            {{ modal.title }}
          </div>
          <SimpleButton icon-left="icon-close" @click="cancel" kind="ghost" />
        </div>
        <div class="content">
          <template v-if="modal.kind === 'builtin'">
            <div v-if="modal.message.length > 0" class="paragraph-s" v-html="modal.message" />

            <label class="prompt text-base-medium" v-if="modal.type === 'prompt'">
              {{ modal.promptedValueName }}
              <TextInput
                ref="promptInput"
                v-model="promptResponse"
                :focus="true"
                @keydown.enter="confirm"
                :type="modal.inputType"
                :pattern="modal.inputPattern"
              />
            </label>

            <label class="prompt text-base-medium" v-if="modal.type === 'multiline-prompt'">
              {{ modal.promptedValueName }}

              <div class="editor">
                <div class="format-actions">
                  <div class="format" @click="richTextEditor?.markBold()">
                    <i class="icon icon-bold" />
                  </div>
                  <div class="format" @click="richTextEditor?.markItalic()">
                    <i class="icon icon-italic" />
                  </div>
                  <div class="format" @click="richTextEditor?.markList()">
                    <i class="icon icon-list" />
                  </div>
                </div>
                <RichTextEditor ref="richTextEditor" v-model:value="promptResponse" :focus="true" />
              </div>
            </label>
          </template>

          <template v-else>
            <component
              :is="modal.body"
              v-bind="{
                ...(modal.bodyProps ?? {}),
                ...modelBindings(modal.models),
                close: modal.close,
                cancel: modal.cancel,
              }"
            />
          </template>
        </div>
        <div class="actions alert" v-if="modal.kind === 'builtin' && modal.type === 'alert'">
          <SimpleButton @click="confirm" :label="modal.confirmText" kind="primary" />
        </div>

        <div class="actions confirm" v-if="modal.kind === 'builtin' && modal.type === 'confirm'">
          <SimpleButton @click="cancel" :label="modal.cancelText" kind="ghost" />
          <SimpleButton
            @click="confirm"
            :label="modal.confirmText"
            kind="primary"
            :class="{ danger: modal.isDangerous }"
          />
        </div>

        <div
          class="actions prompt"
          v-if="
            modal.kind === 'builtin' &&
            (modal.type === 'prompt' || modal.type === 'multiline-prompt')
          "
        >
          <SimpleButton @click="cancel" :label="modal.cancelText" kind="ghost" />
          <SimpleButton
            @click="confirm"
            :label="modal.confirmText"
            kind="primary"
            :disabled="isPromptConfirmButtonDisabled"
          />
        </div>

        <div class="actions component" v-if="modal.kind === 'component' && modal.footer">
          <component
            :is="modal.footer"
            v-bind="{
              ...(modal.footerProps ?? {}),
              ...modelBindings(modal.models),
              close: modal.close,
              cancel: modal.cancel,
            }"
          />
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.container {
  position: fixed;
  z-index: 8000;
  top: 0;
  left: 0;
  display: flex;
  width: 100dvw;
  height: 100dvh;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);

  .dialog {
    position: relative;
    width: 70dvw;
    border: solid var(--stroke-width-md) var(--border-tertiary);
    border-radius: var(--numbers-12);
    background-color: var(--background-primary);

    .header {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      padding: var(--numbers-16);
      border-bottom: solid var(--stroke-width-md) var(--border-tertiary);

      .icon {
        display: inline-flex;
        width: var(--numbers-40);
        height: var(--numbers-40);
        align-items: center;
        justify-content: center;
        border-radius: var(--numbers-6);
        margin-right: var(--numbers-4);
        background-color: var(--background-notice-subtle);
        color: var(--content-brand-2);
      }
    }

    .content {
      display: flex;
      flex-direction: column;
      padding: var(--numbers-16);
      gap: var(--numbers-16);

      .prompt {
        display: flex;
        flex-direction: column;

        .text-input {
          background-color: var(--background-primary);
        }
      }

      .editor {
        display: flex;
        height: 15dvh;
        flex-direction: column;
        padding: var(--numbers-4);
        border-radius: var(--numbers-6);
        background-color: var(--background-secondary);
        gap: var(--numbers-4);

        .format-actions {
          display: flex;
          flex-direction: row;
          justify-content: flex-end;
        }

        .rich-text-editor {
          flex-grow: 1;
        }
      }
    }

    .actions {
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: var(--numbers-16);
      border-top: solid var(--stroke-width-md) var(--border-tertiary);
      gap: var(--numbers-16);

      .skore-button {
        flex-grow: 1;

        &.danger {
          border: solid var(--stroke-width-md) var(--border-negative);
          background-color: var(--background-negative-subtle);
          color: var(--content-negative);
        }
      }
    }
  }
}

.modal-appear-enter-active,
.modal-appear-leave-active {
  transition: opacity var(--animation-duration) var(--animation-easing);

  & .dialog {
    transition: transform var(--animation-duration) var(--animation-easing);
  }
}

.modal-appear-enter-from,
.modal-appear-leave-to {
  opacity: 0;

  & .dialog {
    transform: translateY(33px);
  }
}

@media screen and (width >=801px) {
  .container {
    .dialog {
      width: 60dvw;
    }
  }
}

@media screen and (width >=1025px) {
  .container {
    .dialog {
      width: 50dvw;
    }
  }
}

@media screen and (width >=1279px) {
  .container {
    .dialog {
      width: 40dvw;
    }
  }
}
</style>
